<template>
  <v-data-table
    :headers="headers"
    :items="puppyInfo"
    hide-default-footer
    disable-pagination
    class="elevation-1 full-width"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title>{{ sire.name }}</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="600px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" dark class="mb-2" v-bind="attrs" v-on="on">
              New Puppy
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ formTitle }}</span>
            </v-card-title>

            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <v-select
                      v-model="editedItem.sex"
                      label="Sex"
                      :items="sexChoices"
                      item-text="description"
                      item-value="abbreviation"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-select
                      v-model="editedItem.mainColor"
                      label="Main Colors"
                      :items="allowedColorChoices"
                      item-text="description"
                      item-value="abbreviation"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-checkbox
                      v-model="editedItem.isDead"
                      label="Dead/Culled?"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row>                  
                  <v-col cols="12" sm="12" md="12">
                    <v-text-field
                      outlined
                      label="Breeder Notes"
                      placeholder="Additional information about puppies within the litter, e.g. a name."
                      v-model="editedItem.notes"
                      :rules="[(v) => v.length < maxNotesFieldLength || maxNotesFieldLengthText]"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="close"> Cancel </v-btn>
              <v-btn color="blue darken-1" text :disabled="!isValid" @click="save"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-card-title style="word-break: break-word" class="text-h5"
              >Are you sure you want to delete this puppy?</v-card-title
            >
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="closeDelete"
                >Cancel</v-btn
              >
              <v-btn color="blue darken-1" text @click="deleteItemConfirm"
                >OK</v-btn
              >
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon small class="mr-2" @click="editItem(item)"> mdi-pencil </v-icon>
      <v-icon small @click="deleteItem(item)"> mdi-delete </v-icon>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import Vue from "vue";
export default Vue.extend({
  name: "PuppyTable",
  data: function () {
    return {
      dialog: false,
      dialogDelete: false,
      headers: [
        { text: "Sex", value: "sex" },
        { text: "Main Color", value: "mainColor" },
        { text: "Dead/Culled?", value: "isDead" },
        { text: "Breeder Notes", value: "notes" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      sexChoices: [
        { description: "Male", abbreviation: "M" },
        { description: "Female", abbreviation: "F" },
      ],
      editedIndex: -1,
      editedItem: {
        sex: "M",
        mainColor: "BM",
        isDead: false,
        notes: "",
      },
      defaultItem: {
        sex: "M",
        mainColor: "BM",
        isDead: false,
        notes: "",
      },
      maxNotesFieldLength: 128,
      maxNotesFieldLengthText: "128 characters max"
    };
  },
  methods: {
    editItem(item: any) {
      this.$data.editedIndex = (this as any).puppyInfo.indexOf(item);
      this.$data.editedItem = Object.assign({}, item);
      this.$data.dialog = true;
    },

    deleteItem(item: any) {
      this.$data.editedIndex = (this as any).puppyInfo.indexOf(item);
      this.$data.editedItem = Object.assign({}, item);
      this.$data.dialogDelete = true;
    },

    deleteItemConfirm() {
      (this as any).puppyInfo.splice(this.$data.editedIndex, 1);
      this.$nextTick(() => {
        this.changePuppyInfo((this as any).puppyInfo, this.sire.dogId);
        (this as any).closeDelete();
      });
    },

    close() {
      this.$data.dialog = false;
      this.$nextTick(() => {
        this.$data.editedItem = Object.assign({}, this.$data.defaultItem);
        this.$data.editedIndex = -1;
      });
    },

    closeDelete() {
      this.$data.dialogDelete = false
      this.$nextTick(() => {
        this.$data.editedItem = Object.assign({}, this.$data.defaultItem);
        this.$data.editedIndex = -1;
      });
    },

    save() {
      if (this.$data.editedIndex > -1) {
        Object.assign(
          (this as any).puppyInfo[this.$data.editedIndex],
          this.$data.editedItem
        );
        this.$nextTick(() => {
          this.changePuppyInfo((this as any).puppyInfo, this.sire.dogId);
        });
      } else {
        this.changePuppyInfo(
          [...(this as any).puppyInfo, this.$data.editedItem],
          this.sire.dogId
        );
      }
      (this as any).close();
    },
  },
  computed: {
    allowedColorChoices(): any[] {
      if (this.dam.mainColor?.toUpperCase().includes("RED")
        && this.sire.mainColor?.toUpperCase().includes("RED"))
        return this.mainColorChoices.filter(c => (c as { abbreviation: string, description: string }).description.toUpperCase().includes("RED"));
      else return this.mainColorChoices;
    },
    formTitle() {
      return this.$data.editedIndex === -1 ? "New Puppy" : "Edit Puppy";
    },
    isValid() {
      return this.$data.editedItem.notes.length < this.$data.maxNotesFieldLength;
    }
  },
  props: {
    dam: Object,
    sire: Object,
    puppyInfo: [] as any[],
    changePuppyInfo: Function,
    mainColorChoices: Array,
  },
});
</script>